import {
  createAsyncThunk,
  createSlice,
  SerializedError,
} from '@reduxjs/toolkit';

import auth from '../../../config/auth';
import { axiosDocumentsInstance, axiosInstance } from '../../../config/axios';

import {
  DOCUMENT_TYPE_IDS,
  DOCUMENT_TYPE_NAMES,
} from '../components/documents/constants';

type DocumentType = {
  id: string;
  name: string;
  otherName?: string;
  category: string;
  subcategory: string;
};

export type Document = {
  documentUid: string;
  templateUid?: string;
  lastUpdated: string;
  documentType: DocumentType;
  issueDate: string;
  expiryDate: string;
  updatedAt?: string;
  name?: string;
};

type PersonListType = 'arrival' | 'departure';

export interface State {
  documents: DocumentsState;
}

interface DocumentsState {
  uploadedDocuments: {
    [key: string]: {
      document: Document;
    };
  };
  vesselCertificates: [];
  documents: {
    cargoDocuments?: Document[];
    instructionsDocuments?: Document[];
    localFormalities?: Document[];
    preArrivalDocuments?: Document[];
    preDepartureDocuments?: Document[];
    requestedCertificates?: Document[];
    shipCertificates?: Document[];
  };
  isLoading: Record<string, boolean>;
  uploadCrewListStatus: string;
  uploadCrewPDFStatus: string;
  deleteCrewListFileStatus: string;
  deleteCrewListPDFStatus: string;
  deletePassengersListFileStatus: string;
  passengers: {
    uploadListStatus: string;
    uploadPDFStatus: string;
    deleteListFileStatus: string;
    deletePDFStatus: string;
  };
  errors: Record<string, string | undefined | SerializedError>;
  types: object;
  portInstructions: [];
  previousCrewFile: ArrayBuffer | null;
  previousCrewFileName: string | null;
}

const initialState: DocumentsState = {
  uploadedDocuments: {},
  vesselCertificates: [],
  documents: {},
  isLoading: {},
  uploadCrewListStatus: '',
  uploadCrewPDFStatus: '',
  deleteCrewListFileStatus: '',
  deleteCrewListPDFStatus: '',
  deletePassengersListFileStatus: '',
  passengers: {
    uploadListStatus: '',
    uploadPDFStatus: '',
    deleteListFileStatus: '',
    deletePDFStatus: '',
  },
  types: {},
  portInstructions: [],
  errors: {},
  previousCrewFile: null,
  previousCrewFileName: null,
};

const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED'];

const getDocKey = (documentTypeId: string, documentName?: string) => {
  const key = `${documentTypeId}${documentName ? `-${documentName}` : ''}`;
  return key;
};

export const getDocumentTypes = createAsyncThunk(
  'documents/types',
  async (_: void, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(`/document-types`, {
        headers,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const selectDocumentsTypes = (state: State) => state.documents.types;

export const getPortInstructions = createAsyncThunk<
  { data: [] },
  {
    portUnloCode: string;
    agentCompanyUid: string;
  }
>(
  'portInstructions',
  async ({ portUnloCode, agentCompanyUid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(
        `/portcall-instructions?unlocode=${portUnloCode}&companyUid=${agentCompanyUid}`,
        {
          headers,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const generatePreArrivalDocuments = createAsyncThunk<
  object,
  {
    uid: string;
  }
>('pre-arrival/generate', async ({ uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  try {
    const response = await axiosInstance.put(
      `portcalls/${uid}/pre-arrival-documents`,
      {},
      {
        headers,
      }
    );

    return { status: response.status, message: response.statusText };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const generatePreDepartureDocuments = createAsyncThunk<
  object,
  {
    uid: string;
  }
>('pre-departure/generate', async ({ uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  try {
    const response = await axiosInstance.put(
      `portcalls/${uid}/pre-departure-documents`,
      {},
      {
        headers,
      }
    );

    return { status: response.status, message: response.statusText };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getRequiredDocuments = createAsyncThunk<
  { documents: { [key: string]: Document[] } },
  {
    uid: string;
  }
>('documents/getRequiredDocuments', async ({ uid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();
  try {
    const response = await axiosInstance.get(`portcalls/${uid}/documents`, {
      headers,
    });

    return {
      documents: response.data.data,
    };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getVesselCertificates = createAsyncThunk<
  { documents: [] },
  {
    vesselUid: string;
  }
>(
  'documents/getVesselCertificates',
  async ({ vesselUid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      const response = await axiosInstance.get(
        `/vessels/${vesselUid}/ship-certificates`,
        {
          headers,
        }
      );

      return {
        documents: response.data.data,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadLocalFormalities = createAsyncThunk<
  object,
  {
    file: File;
    documentUid: string;
    documentTypeId: string;
    category: string;
    portcallId: string;
    documentTypeName: string;
    templateUid: string;
  },
  { state: State }
>(
  'documents/uploadLocalFormalities',
  async (
    {
      file,
      documentUid,
      documentTypeId,
      category,
      portcallId,
      documentTypeName,
      templateUid,
    },
    { getState, rejectWithValue }
  ) => {
    const headers = auth.getHeaders();
    const { documents } = getState();

    const formData = new FormData();
    formData.append('file', file);

    const url = `document/${documentUid}`;
    try {
      await axiosDocumentsInstance.put(url, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }

    const cat = category as keyof typeof documents.documents;
    const currentDocuments: Document[] = Array.isArray(documents.documents[cat])
      ? (documents.documents[cat] as unknown as Document[])
      : [];

    const newDocuments: Document[] = currentDocuments.map((document) => {
      const isSameDoc =
        document.documentType.id === documentTypeId &&
        document.templateUid === templateUid;

      if (!isSameDoc) return document;

      return {
        ...document,
        documentUid,
        lastUpdated: new Date().toISOString(),
        documentType: {
          ...document.documentType,
          name: documentTypeName,
        },
      };
    });

    if (newDocuments) {
      try {
        await axiosInstance.put(
          `portcalls/${portcallId}/documents`,
          {
            [category]: newDocuments,
          },
          {
            headers,
          }
        );
      } catch (error) {
        return rejectWithValue(error);
      }
    }

    return {
      [category]: newDocuments,
    };
  }
);

export const uploadDocument = createAsyncThunk<
  { [category: string]: Document[] },
  {
    file: File;
    documentUid: string;
    documentTypeId: string;
    category: string;
    portcallId: string;
    documentTypeName: string;
    templateUid?: string;
    expiryDate?: string;
    issueDate?: string;
    documentCategory: string;
    documentSubCategory: string;
    isShipCertificate?: boolean;
    checkPdf?: boolean;
    isCrewFile?: boolean;
    documentOtherName?: string | null;
  },
  { state: State }
>(
  'documents/uploadDocument',
  async (
    {
      file,
      documentUid,
      documentTypeId,
      category,
      portcallId,
      expiryDate,
      issueDate,
      documentCategory,
      documentSubCategory,
      documentTypeName,
      isShipCertificate = false,
      checkPdf = true,
      isCrewFile = false,
      documentOtherName = null,
    },
    { getState, rejectWithValue }
  ) => {
    const headers = auth.getHeaders();
    const { documents } = getState();
    const isPDF = file.type === 'application/pdf';

    if (
      ['preArrivalDocuments', 'preDepartureDocuments'].includes(category) &&
      (!checkPdf || (checkPdf && !isPDF))
    ) {
      documentTypeName = DOCUMENT_TYPE_NAMES.CREW_EXCEL_DOCUMENT;
    }

    const formData = new FormData();
    formData.append('file', file);

    let url = `document/${documentUid}`;
    if (isShipCertificate) {
      url = `document/${documentUid}?secure=true`;
    }

    try {
      const response = await axiosDocumentsInstance.put(url, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (isShipCertificate && response?.data?.status === 'Warning') {
        documentUid = response.data?.data?.originalDocumentUid;
      }
    } catch (error) {
      return rejectWithValue(error);
    }

    const cat = category as keyof typeof documents.documents;
    const currentDocuments: Document[] = Array.isArray(documents.documents[cat])
      ? (documents.documents[cat] as unknown as Document[])
      : [];

    const isExcel = (name: string) =>
      name.includes(DOCUMENT_TYPE_NAMES.CREW_EXCEL_DOCUMENT);

    const existingDocument = currentDocuments.find((doc) => {
      const existingIsPDF = !isExcel(doc.documentType.name);
      if (isCrewFile) {
        return (
          doc.documentType.id === documentTypeId &&
          ((isPDF && existingIsPDF) || (!isPDF && !existingIsPDF))
        );
      } else {
        return doc.documentType.id === documentTypeId;
      }
    });

    let newDocuments: Document[];

    if (existingDocument) {
      if (
        documentTypeId !== 'CERT-OTHER' &&
        documentTypeId !== 'LOCAL-OTHER-0001'
      ) {
        newDocuments = currentDocuments.map((doc: Document) => {
          let sameType = doc.documentType.id === documentTypeId;
          if (isCrewFile) {
            sameType =
              sameType &&
              ((isPDF && !isExcel(doc.documentType.name)) ||
                (!isPDF && isExcel(doc.documentType.name)));
          }
          if (!sameType) return doc;

          const updatedDoc: Document = {
            ...doc,
            documentUid,
            lastUpdated: new Date().toISOString(),
            documentType: {
              ...doc.documentType,
              name: documentTypeName,
            },
            ...(category === 'requestedCertificates' && {
              issueDate,
              expiryDate,
            }),
          };

          return updatedDoc;
        });
      } else {
        const certOtherIndex = currentDocuments.findIndex((doc) => {
          return (
            doc.documentType.id === documentTypeId &&
            doc.documentType.name === documentTypeName &&
            ((documentTypeId === 'LOCAL-OTHER-0001' &&
              doc.documentType.otherName === documentOtherName) ||
              documentTypeId === 'CERT-OTHER')
          );
        });
        const updatedDoc: Document = {
          documentUid,
          lastUpdated: new Date().toISOString(),
          name: documentTypeName,
          documentType: {
            id: documentTypeId,
            name: documentTypeName,
            otherName: documentOtherName ?? documentTypeName,
            category: documentCategory,
            subcategory: documentSubCategory,
          },
          issueDate: issueDate ?? '',
          expiryDate: expiryDate ?? '',
        };
        const arrayCopy = Array.from(currentDocuments);
        arrayCopy.splice(certOtherIndex, 1, updatedDoc);
        newDocuments = arrayCopy;
      }
    } else {
      const newDoc: Document = {
        documentUid,
        lastUpdated: new Date().toISOString(),
        documentType: {
          id: documentTypeId,
          name: documentTypeName,
          category: documentCategory,
          subcategory: documentSubCategory,
        },
        issueDate: '',
        expiryDate: '',
        templateUid: '',
        updatedAt: '',
        ...(category === 'requestedCertificates' && {
          issueDate,
          expiryDate,
        }),
      };
      newDocuments = [...currentDocuments, newDoc];
    }

    if (newDocuments) {
      try {
        await axiosInstance.put(
          `portcalls/${portcallId}/documents`,
          {
            [category]: newDocuments,
          },
          {
            headers,
          }
        );
      } catch (error) {
        return rejectWithValue(error);
      }
    }

    return {
      [category]: newDocuments,
    };
  }
);

export const requestDocuments = createAsyncThunk<
  {
    category: string;
    data: Document[];
  },
  {
    uid: string;
    category: string;
    data: Document[];
  },
  { state: State }
>(
  'documents/requestDocuments',
  async ({ uid, category, data }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    try {
      await axiosInstance.put(
        `portcalls/${uid}/documents`,
        {
          [category]: data,
        },
        {
          headers,
        }
      );
      return { category: category, data: data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDocument = createAsyncThunk<
  {
    data: {
      attributes: Document;
    };
  },
  {
    uiid: string;
    documentTypeId: string;
  }
>('documents/getDocument', async ({ uiid }, { rejectWithValue }) => {
  const headers = auth.getHeaders();

  try {
    const response = await axiosDocumentsInstance.get(`document/${uiid}`, {
      headers,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const downloadDocument = createAsyncThunk<
  object,
  { uiid: string; fileName: string }
>(
  'documents/downloadDocument',
  async ({ uiid, fileName }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    let response;
    try {
      response = await axiosDocumentsInstance.get(`document/download/${uiid}`, {
        headers,
        responseType: 'arraybuffer',
      });
    } catch (error) {
      return rejectWithValue(error);
    }
    const uint8Array = new Uint8Array(response.data);

    const file = new Blob([uint8Array.buffer], {
      type: response.headers['content-type'] ?? 'application/pdf',
    });
    const url = URL.createObjectURL(file);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();

    // Clean up the temporary URL and link
    URL.revokeObjectURL(url);
    link.remove();
  }
);

export const openDocument = createAsyncThunk<
  object,
  { uiid: string; portcallId: string }
>(
  'documents/openDocument',
  async ({ uiid, portcallId }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    let response;
    try {
      response = await axiosDocumentsInstance.get(`document/download/${uiid}`, {
        headers,
        responseType: 'arraybuffer',
      });
    } catch (error) {
      return rejectWithValue(error);
    }

    const contentType = response.headers['content-type'];
    const uint8Array = new Uint8Array(response.data);
    const file = new Blob([uint8Array.buffer], { type: contentType });
    const url = URL.createObjectURL(file);

    if (contentType === 'application/pdf') {
      // If it's a PDF, open it in a new tab
      window.open(url, '_blank');
    } else {
      // If it's not a PDF, trigger a download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${portcallId}.${getExtension(contentType)}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
);

export const downloadCrewListTemplate = createAsyncThunk<
  object,
  { uid: string; listType: PersonListType }
>(
  'documents/downloadCrewListTemplate',
  async ({ uid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    let response;
    try {
      response = await axiosInstance.get(`portcalls/${uid}/crew/file`, {
        headers,
        responseType: 'arraybuffer',
        params: {
          list: listType,
          template: true,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }
    const uint8Array = new Uint8Array(response.data);

    const file = new Blob([uint8Array.buffer], {
      type:
        response.headers['content-type'] ??
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(file);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `crew-list-${listType}-template.xlsx`;
    link.click();

    // Clean up the temporary URL and link
    URL.revokeObjectURL(url);
    link.remove();
  }
);

export const downloadCrewList = createAsyncThunk<
  object,
  { uid: string; listType: PersonListType; filename: string }
>(
  'documents/downloadCrewList',
  async ({ uid, listType, filename }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    let response;
    try {
      response = await axiosInstance.get(`portcalls/${uid}/crew/file`, {
        headers,
        responseType: 'arraybuffer',
        params: {
          list: listType,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }
    const uint8Array = new Uint8Array(response.data);

    const file = new Blob([uint8Array.buffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(file);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    // Clean up the temporary URL and link
    URL.revokeObjectURL(url);
    link.remove();
  }
);

export const downloadPreviousCrewList = createAsyncThunk<
  object,
  { uid: string; listType: PersonListType }
>(
  'documents/downloadPreviousCrewList',
  async ({ uid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    let response;
    try {
      response = await axiosInstance.get(
        `portcalls/${uid}/crew/file/generate`,
        {
          headers,
          responseType: 'arraybuffer',
          params: {
            list: listType,
          },
        }
      );
    } catch (error) {
      return rejectWithValue(error);
    }
    const uint8Array = new Uint8Array(response.data);
    const contentType = response?.headers?.['content-type'];
    const contentDisposition = response?.headers?.['content-disposition'];
    const matches = /(?<=filename=).*/.exec(contentDisposition);
    const fileName = matches?.[0] || 'downloaded-file';
    const file = new Blob([uint8Array.buffer], { type: 'text/csv' });
    return {
      file: file,
      fileName: fileName,
      contentType: contentType,
      data: uint8Array.buffer,
    };
  }
);

export const uploadCrewList = createAsyncThunk<
  object,
  {
    portcallUid: string;
    file: File;
    listType: PersonListType;
  },
  { state: State }
>(
  'documents/uploadCrewList',
  async ({ file, portcallUid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    const formData = new FormData();
    formData.append('file', file);

    const url = `portcalls/${portcallUid}/crew/file`;
    try {
      await axiosInstance.post(url, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
        params: {
          list: listType,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }

    return {
      crewList: {
        lastUpdated: new Date().toISOString(),
      },
    };
  }
);

export const deleteCrewListFile = createAsyncThunk<
  object,
  { uid: string; listType: PersonListType }
>(
  'documents/deleteCrewListFile',
  async ({ uid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      await axiosInstance.delete(`portcalls/${uid}/crew/file`, {
        headers,
        params: {
          list: listType,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteCrewListPDF = createAsyncThunk<
  object,
  { uid: string; documentUid: string }
>(
  'documents/deleteCrewListPDF',
  async ({ uid, documentUid }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      await axiosInstance.delete(`portcalls/${uid}/documents/${documentUid}`, {
        headers,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const downloadPassengersList = createAsyncThunk<
  object,
  {
    uid: string;
    listType: PersonListType;
    template?: boolean;
    filename?: string;
  }
>(
  'documents/downloadPassengersList',
  async (
    { uid, listType, template = false, filename },
    { rejectWithValue }
  ) => {
    const headers = auth.getHeaders();

    let response;
    try {
      response = await axiosInstance.get(`portcalls/${uid}/passengers/file`, {
        headers,
        responseType: 'arraybuffer',
        params: {
          list: listType,
          template,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }
    const uint8Array = new Uint8Array(response.data);

    const file = new Blob([uint8Array.buffer], {
      type:
        response.headers['content-type'] ??
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(file);

    // Create a temporary link element
    let name = `passengers-list-${listType}.xlsx`;
    if (template) {
      name = `passengers-list-${listType}-template.xlsx`;
    } else if (filename) {
      name = filename;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();

    // Clean up the temporary URL and link
    URL.revokeObjectURL(url);
    link.remove();
  }
);

export const uploadPassengersList = createAsyncThunk<
  object,
  {
    portcallUid: string;
    file: File;
    listType: PersonListType;
  },
  { state: State }
>(
  'documents/uploadPassengersList',
  async ({ file, portcallUid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();

    const formData = new FormData();
    formData.append('file', file);

    const url = `portcalls/${portcallUid}/passengers/file`;
    try {
      await axiosInstance.post(url, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
        params: {
          list: listType,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }

    return {
      crewList: {
        lastUpdated: new Date().toISOString(),
      },
    };
  }
);

export const deletePassengersListFile = createAsyncThunk<
  object,
  { uid: string; listType: PersonListType }
>(
  'documents/deletePassengersListFile',
  async ({ uid, listType }, { rejectWithValue }) => {
    const headers = auth.getHeaders();
    try {
      await axiosInstance.delete(`portcalls/${uid}/passengers/file`, {
        headers,
        params: {
          list: listType,
        },
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Helper function to get the file extension based on content type
const getExtension = (contentType: string) => {
  switch (contentType) {
    case 'text/csv':
      return 'csv';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx';
    case 'application/vnd.ms-excel':
      return 'xls';
    default:
      return 'bin';
  }
};

export const removeDocument = createAsyncThunk<
  object,
  {
    documentUid: string;
    documentTypeId: string;
    category: string;
    portcallId: string;
    templateUid: string;
  },
  { state: State }
>(
  'documents/removeDocument',
  async (
    { documentUid, documentTypeId, category, portcallId },
    { getState }
  ) => {
    const headers = auth.getHeaders();
    const { documents } = getState();

    const cat = category as keyof typeof documents.documents;
    const currentDocuments: Document[] = Array.isArray(documents.documents[cat])
      ? (documents.documents[cat] as unknown as Document[])
      : [];

    // Filter documents based on the presence of documentUid
    const newDocuments = currentDocuments.filter((document: Document) => {
      if (documentUid) {
        return document.documentUid !== documentUid;
      }
      return document.documentType.id !== documentTypeId;
    });

    // Update documents on the server
    await axiosInstance.put(
      `portcalls/${portcallId}/documents`,
      {
        [category]: newDocuments,
      },
      {
        headers,
      }
    );

    return {
      [category]: newDocuments,
    };
  }
);

export const removeUploadedDocument = createAsyncThunk<
  object,
  {
    documentUid: string;
    category: string;
    portcallId: string;
  },
  { state: State }
>(
  'documents/removeDocument',
  async (
    { documentUid, category, portcallId },
    { getState, rejectWithValue }
  ) => {
    const headers = auth.getHeaders();
    const { documents } = getState();

    const cat = category as keyof typeof documents.documents;
    const currentDocuments: Document[] = Array.isArray(documents.documents[cat])
      ? (documents.documents[cat] as unknown as Document[])
      : [];

    const newDocuments = currentDocuments.map((document: Document) => {
      if (document.documentUid === documentUid) {
        const rest = Object.fromEntries(
          Object.entries(document).filter(
            ([key]) =>
              !['documentUid', 'issueDate', 'expiryDate', 'updatedAt'].includes(
                key
              )
          )
        );
        return rest;
      }
      return document;
    });

    try {
      await axiosInstance.put(
        `portcalls/${portcallId}/documents`,
        {
          [category]: newDocuments,
        },
        {
          headers,
        }
      );
    } catch (error) {
      return rejectWithValue(error);
    }

    return {
      [category]: newDocuments,
    };
  }
);

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRequiredDocuments.fulfilled, (state, action) => {
        const { documents } = action.payload;
        state.documents = documents;
      })
      .addCase(getVesselCertificates.fulfilled, (state, action) => {
        const { documents } = action.payload;
        state.vesselCertificates = documents;
      })
      .addCase(requestDocuments.fulfilled, (state, action) => {
        if (action.payload) {
          state.documents = {
            ...state.documents,
            [action.payload.category]: action.payload.data,
          };
        }
      })
      .addCase(
        getDocument.pending,
        (
          state,
          {
            meta: {
              arg: { documentTypeId },
            },
          }
        ) => {
          state.isLoading[documentTypeId] = true;
          state.errors[documentTypeId] = undefined;
        }
      )
      .addCase(
        getDocument.fulfilled,
        (
          state,
          {
            payload,
            meta: {
              arg: { documentTypeId },
            },
          }
        ) => {
          const { documentUid } = payload.data.attributes;

          state.uploadedDocuments[documentUid] = {
            document: payload.data.attributes,
          };

          state.isLoading[documentTypeId] = false;
        }
      )
      .addCase(
        getDocument.rejected,
        (
          state,
          {
            meta: {
              arg: { documentTypeId },
            },
            error,
          }
        ) => {
          state.isLoading[documentTypeId] = false;
          state.errors[documentTypeId] = error;
        }
      )
      .addCase(
        uploadDocument.pending,
        (
          state,
          {
            meta: {
              arg: { documentTypeId, documentTypeName },
            },
          }
        ) => {
          if (documentTypeId === 'CERT-OTHER') {
            state.isLoading[getDocKey(documentTypeId, documentTypeName)] = true;
          } else {
            state.isLoading[documentTypeId] = true;
          }
          state.errors[documentTypeId] = undefined;
        }
      )
      .addCase(
        uploadDocument.fulfilled,
        (
          state,
          {
            payload,
            meta: {
              arg: { documentTypeId, documentTypeName },
            },
          }
        ) => {
          state.documents = {
            ...state.documents,
            ...payload,
          };

          if (documentTypeId === 'CERT-OTHER') {
            state.isLoading[getDocKey(documentTypeId, documentTypeName)] =
              false;
          } else {
            state.isLoading[documentTypeId] = false;
          }
        }
      )
      .addCase(
        uploadDocument.rejected,
        (
          state,
          {
            meta: {
              arg: { documentTypeId, documentTypeName },
            },
            error,
          }
        ) => {
          if (documentTypeId === 'CERT-OTHER') {
            state.isLoading[getDocKey(documentTypeId, documentTypeName)] =
              false;
          } else {
            state.isLoading[documentTypeId] = false;
          }
          if (NETWORK_ERROR_CODES.indexOf(error.code ?? '') !== -1) {
            state.errors[documentTypeId] = {
              message:
                'Timeout. Please check your internet connection and try again.',
            };
          } else {
            state.errors[documentTypeId] = error;
          }
        }
      )
      .addCase(
        uploadLocalFormalities.pending,
        (
          state,
          {
            meta: {
              arg: { templateUid },
            },
          }
        ) => {
          state.isLoading[templateUid] = true;
        }
      )
      .addCase(
        uploadLocalFormalities.fulfilled,
        (
          state,
          {
            payload,
            meta: {
              arg: { templateUid },
            },
          }
        ) => {
          state.documents = {
            ...state.documents,
            ...payload,
          };

          state.isLoading[templateUid] = false;
        }
      )
      .addCase(
        removeDocument.pending,
        (
          state,
          {
            meta: {
              arg: { documentTypeId, templateUid },
            },
          }
        ) => {
          if (documentTypeId === DOCUMENT_TYPE_IDS.LOCAL_FORMALITY_OTHER)
            state.isLoading[templateUid] = true;
          else state.isLoading[documentTypeId] = true;
        }
      )
      .addCase(
        removeDocument.fulfilled,
        (
          state,
          {
            payload,
            meta: {
              arg: { documentTypeId, templateUid },
            },
          }
        ) => {
          state.documents = {
            ...state.documents,
            ...payload,
          };

          if (documentTypeId === DOCUMENT_TYPE_IDS.LOCAL_FORMALITY_OTHER)
            state.isLoading[templateUid] = false;
          else state.isLoading[documentTypeId] = false;
        }
      )
      .addCase(getDocumentTypes.fulfilled, (state, { payload }) => {
        state.types = payload.data;
      })
      .addCase(getPortInstructions.fulfilled, (state, action) => {
        if (action.payload) {
          state.portInstructions = action.payload.data;
        }
      })
      .addCase(uploadCrewList.pending, (state) => {
        state.uploadCrewListStatus = 'loading';
      })
      .addCase(uploadCrewList.fulfilled, (state) => {
        state.uploadCrewListStatus = 'succeeded';
      })
      .addCase(uploadCrewList.rejected, (state) => {
        state.uploadCrewListStatus = 'failed';
      })
      .addCase(deleteCrewListFile.pending, (state) => {
        state.deleteCrewListFileStatus = 'loading';
      })
      .addCase(deleteCrewListFile.fulfilled, (state) => {
        state.deleteCrewListFileStatus = 'succeeded';
      })
      .addCase(deleteCrewListFile.rejected, (state) => {
        state.deleteCrewListFileStatus = 'failed';
      })
      .addCase(deleteCrewListPDF.pending, (state) => {
        state.deleteCrewListPDFStatus = 'loading';
      })
      .addCase(deleteCrewListPDF.fulfilled, (state) => {
        state.deleteCrewListPDFStatus = 'succeeded';
      })
      .addCase(deleteCrewListPDF.rejected, (state) => {
        state.deleteCrewListPDFStatus = 'failed';
      })
      .addCase(uploadPassengersList.pending, (state) => {
        state.passengers.uploadListStatus = 'loading';
      })
      .addCase(uploadPassengersList.fulfilled, (state) => {
        state.passengers.uploadListStatus = 'succeeded';
      })
      .addCase(uploadPassengersList.rejected, (state) => {
        state.passengers.uploadListStatus = 'failed';
      })
      .addCase(deletePassengersListFile.pending, (state) => {
        state.deletePassengersListFileStatus = 'loading';
      })
      .addCase(deletePassengersListFile.fulfilled, (state) => {
        state.deletePassengersListFileStatus = 'succeeded';
      })
      .addCase(deletePassengersListFile.rejected, (state) => {
        state.deletePassengersListFileStatus = 'failed';
      });
  },
});

export const selectIsLoading = (
  state: State,
  documentTypeId: string,
  documentName?: string
) => {
  return state.documents.isLoading[getDocKey(documentTypeId, documentName)];
};

export const selectIsError = (state: State, documentTypeId: string) => {
  return state.documents.errors?.[documentTypeId] ?? undefined;
};

export const selectDocuments = (state: State) => state.documents;

export const selectVesselCertificates = (state: State) =>
  state.documents.vesselCertificates;

export const selectUploadedDocument = (
  state: State,
  uiid: string
): { document: Document | undefined } => {
  return state.documents.uploadedDocuments[uiid] ?? { document: undefined };
};
export const selectSafetyDocumentsByTitle = (state: State) => {
  const safetyDocumentsByTitle: Record<string, Document[]> = {};
  const { localFormalities } = state.documents.documents;

  if (Array.isArray(localFormalities)) {
    localFormalities.forEach((document) => {
      const key = document.documentType.subcategory;

      if (safetyDocumentsByTitle[key]) {
        safetyDocumentsByTitle[key].push(document);
      } else {
        safetyDocumentsByTitle[key] = [document];
      }
    });
  }

  return safetyDocumentsByTitle;
};

export const selectCargoDocumentsByTitle = (state: State) => {
  const cargoDocumentsByTitle: Record<string, Document[]> = {};
  const { cargoDocuments } = state.documents.documents;

  if (Array.isArray(cargoDocuments)) {
    cargoDocuments.forEach((document) => {
      const key = document.documentType.subcategory;
      if (cargoDocumentsByTitle[key]) {
        cargoDocumentsByTitle[key].push(document);
      } else {
        cargoDocumentsByTitle[key] = [document];
      }
    });
  }

  return cargoDocumentsByTitle;
};

export const selectShipCertificatesByTitle = (state: State) => {
  const shipCertificatesByTitle: Record<string, Document[]> = {};
  const { requestedCertificates } = state.documents.documents;

  if (Array.isArray(requestedCertificates)) {
    requestedCertificates.forEach((document) => {
      const key = document.documentType.subcategory;
      if (shipCertificatesByTitle[key]) {
        shipCertificatesByTitle[key].push(document);
      } else {
        shipCertificatesByTitle[key] = [document];
      }
    });
  }

  return shipCertificatesByTitle;
};

export default documentsSlice.reducer;
